// Web build of CachedImage. The native version caches remote images to disk
// with expo-file-system, which has no web implementation — the browser's own
// HTTP cache already does that job, so this renders a plain <Image> and only
// keeps the loading/failure states the callers rely on.

import React, { useState } from "react";
import {
	ActivityIndicator,
	Image,
	StyleProp,
	ImageStyle,
	View,
} from "react-native";
import { isResolvedImageSource } from "../utils/ImageFunctions";

interface CachedImageProps {
	source: { uri: string };
	cacheKey: string;
	style: StyleProp<ImageStyle>;
	resizeMode?: "contain" | "cover";
}

/**
 * Callers build a URI by concatenating an API path with an image id. When the
 * id is already a resolved URL — which is what the mock fixtures provide — that
 * concatenation produces "https://api.example.com/users/discounts//static/...".
 * Recover the usable part rather than requesting a URL that cannot exist.
 */
const resolveUri = (uri?: string): string | undefined => {
	if (!uri) return undefined;
	if (isResolvedImageSource(uri) && !uri.includes("/static/")) return uri;

	const assetIndex = uri.indexOf("/static/");
	if (assetIndex > 0) return uri.slice(assetIndex);

	const dataIndex = uri.indexOf("data:");
	if (dataIndex > 0) return uri.slice(dataIndex);

	return uri;
};

const CachedImage = ({ source, style, resizeMode }: CachedImageProps) => {
	const uri = resolveUri(source?.uri);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	if (!uri || failed) {
		return <View style={style as any} />;
	}

	return (
		<>
			{loading && (
				<View
					style={{
						...(style as any),
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<ActivityIndicator size={33} />
				</View>
			)}
			<Image
				source={{ uri }}
				style={[style, loading && { width: 0, height: 0 }] as any}
				resizeMode={resizeMode ?? "contain"}
				onLoad={() => setLoading(false)}
				onError={() => {
					setLoading(false);
					setFailed(true);
				}}
			/>
		</>
	);
};

export default CachedImage;
